"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePassportProvider } from "@/context";
import { useGetWalletGems } from "@/hooks/api";
import { useUserActivities } from "@/hooks/useUserActivities";
import { Gem } from "lucide-react";
import { useMemo } from "react";
import { useState } from "react";
import { GemsFilters } from "./gems-filters";
import { GemsList } from "./gems-list";

interface GemsHistoryProps {
  className?: string;
}

const GAME_QUEST_CATEGORY = "in_game";
const ALL_QUEST_CATEGORY = "all";

export function GemsHistory({ className }: GemsHistoryProps) {
  const { walletAddress } = usePassportProvider();
  const [questType, setQuestType] = useState(ALL_QUEST_CATEGORY);
  const [searchQuery, setSearchQuery] = useState("");

  const { activities, isLoading, error } = useUserActivities(
    walletAddress || null,
  );
  const { walletGems, isLoading: isGemsLoading } = useGetWalletGems(
    walletAddress || null,
    false,
  );

  // Apply search and quest type filters (useUserActivities already filters reward types)
  const rewardsActivities = useMemo(() => {
    if (!activities || activities.length === 0) {
      return [];
    }

    let filteredActivities = [...activities];

    // Apply quest type filter
    if (questType === GAME_QUEST_CATEGORY) {
      filteredActivities = filteredActivities.filter((activity) =>
        activity.quest_categories.includes("in_game"),
      );
    }

    // Apply search filter
    if (searchQuery) {
      filteredActivities = filteredActivities.filter(
        (activity) =>
          activity.quest_name
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          activity.game_name?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    return filteredActivities;
  }, [activities, questType, searchQuery]);

  return (
    <div className={className}>
      <Accordion
        type="single"
        collapsible
        defaultValue="gems-history"
        className="w-full overscroll-contain"
      >
        <AccordionItem value="gems-history" className="border-0">
          <AccordionTrigger className="text-white/80 text-xs hover:text-white hover:font-medium py-2 !no-underline hover:!no-underline [&[data-state=open]]:!no-underline decoration-none hover:decoration-none">
            <div className="flex items-center justify-between text-sm w-full mr-4 text-white">
              <span>Gems History</span>
              <div className="flex items-center gap-2">
                <Gem className="w-4 h-4" />
                <span className="text-xs">
                  {isGemsLoading
                    ? "..."
                    : `${walletGems?.gems?.toLocaleString() || "0"}`}
                </span>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <GemsFilters
              searchQuery={searchQuery}
              questType={questType}
              onSearchChange={setSearchQuery}
              onQuestTypeChange={setQuestType}
            />

            <GemsList
              activities={rewardsActivities}
              isLoading={isLoading}
              error={error}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
